import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, fmtError } from "../api";
import { LayoutDashboard, RefreshCw, ArrowLeft, MapPin, ChevronRight, Building2, Inbox } from "lucide-react";

// Status-Kacheln in derselben Reihenfolge/Benennung wie in der Standortansicht.
const STATUS = [
  { key: "open", label: "Offen", bg: "#FEF2F2", color: "#B91C1C", dot: "#DC2626" },
  { key: "in_progress", label: "In Arbeit", bg: "#EFF6FF", color: "#1D4ED8", dot: "#2563EB" },
  { key: "completed", label: "Erledigt", bg: "#F0FDF4", color: "#15803D", dot: "#16A34A" },
  { key: "external_service_required", label: "Fachfirma", bg: "#FFFBEB", color: "#B45309", dot: "#D97706" },
];

const initials = (t) => (t || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "?";

const fmtDate = (iso) => {
  try {
    return new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Berlin" }).format(new Date(iso));
  } catch { return ""; }
};

function Tile({ tile, onOpen }) {
  const open = tile.counts?.open || 0;
  return (
    <div data-testid={`dashboard-tile-${tile.perm_id}`}
      className="card-wd p-4 w-full flex flex-col gap-3"
      style={{ border: `1px solid ${open > 0 ? "#FED7AA" : "#E2E8F0"}` }}>
      <button type="button" data-testid={`dashboard-tile-open-${tile.perm_id}`} onClick={() => onOpen(tile)}
        className="flex items-center gap-3 text-left"
        style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}>
        <span className="rounded-full flex items-center justify-center flex-shrink-0 font-bold text-sm"
          style={{ width: 42, height: 42, background: "#FFEAD6", color: "#C2410C" }}>{initials(tile.title)}</span>
        <div className="min-w-0 flex-1">
          <div className="font-extrabold text-sm truncate" data-testid={`dashboard-tile-title-${tile.perm_id}`}>{tile.title}</div>
          <div className="text-xs truncate" style={{ color: "#94A3B8" }}>
            {tile.total > 0 ? `${tile.total} Meldung(en)` : "Keine Meldungen"}
            {tile.latest_modified ? ` · ${fmtDate(tile.latest_modified)}` : ""}
          </div>
        </div>
        <span className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ background: "#F1F5F9" }}>
          <ChevronRight size={15} color="#64748B" />
        </span>
      </button>
      <div className="grid grid-cols-2 gap-2">
        {STATUS.map((s) => {
          const n = tile.counts?.[s.key] || 0;
          const clickable = n > 0;
          const inner = (
            <>
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold" style={{ color: clickable ? s.color : "#94A3B8" }}>
                <span className="inline-block rounded-full" style={{ width: 7, height: 7, background: clickable ? s.dot : "#CBD5E1" }} />
                {s.label}
              </span>
              <span className="text-sm font-extrabold tabular-nums" style={{ color: clickable ? s.color : "#94A3B8" }}>{n}</span>
            </>
          );
          return clickable ? (
            <button key={s.key} type="button" data-testid={`dashboard-stat-${tile.perm_id}-${s.key}`}
              onClick={() => onOpen(tile, s.key)} title={`${s.label} öffnen`}
              className="flex items-center justify-between rounded-xl px-3 py-2"
              style={{ background: s.bg, border: `1px solid ${s.bg}`, width: "100%", cursor: "pointer", transition: "filter .15s ease" }}>
              {inner}
            </button>
          ) : (
            <div key={s.key} data-testid={`dashboard-stat-${tile.perm_id}-${s.key}`}
              className="flex items-center justify-between rounded-xl px-3 py-2"
              style={{ background: "#F8FAFC", opacity: 0.6 }}>
              {inner}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const token = sessionStorage.getItem("mag_token");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    if (!token) { navigate("/login"); return; }
    setLoading(true); setError("");
    api.get("/magauth/dashboard", { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setData(data))
      .catch((e) => { setError(fmtError(e, "Dashboard konnte nicht geladen werden")); setData(null); })
      .finally(() => setLoading(false));
  }, [token, navigate]);

  useEffect(() => { load(); }, [load]);

  const groups = useMemo(() => {
    const tiles = data?.tiles || [];
    const map = new Map();
    for (const t of tiles) {
      const key = t.parent_title || "Ohne übergeordnetes Objekt";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(t);
    }
    return [...map.entries()];
  }, [data]);

  const totals = data?.totals || {};
  const openTile = (t, status) => navigate(status ? `/meldungen/${t.perm_id}?status=${status}` : `/meldungen/${t.perm_id}`);

  return (
    <div className="container-wd py-8" data-testid="dashboard-page">
      <button className="btn-secondary mb-4" data-testid="dashboard-back" onClick={() => navigate("/objekte")}
        style={{ padding: "6px 12px", fontSize: 13 }}>
        <ArrowLeft size={14} /> Zurück zu den Objekten
      </button>

      <div className="flex justify-between items-start mb-5 flex-wrap gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-extrabold flex items-center gap-2">
            <span className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: "#FFF7ED" }}>
              <LayoutDashboard size={18} color="#F97316" />
            </span>
            Dashboard
          </h1>
          <p className="text-sm mt-1" style={{ color: "#64748B" }} data-testid="dashboard-subtitle">
            Status aller Standorte auf einen Blick{data?.offline ? " · gespeicherter Stand" : ""}
          </p>
        </div>
        <button className="btn-secondary" data-testid="dashboard-refresh" onClick={load} disabled={loading}>
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Aktualisieren
        </button>
      </div>

      {/* Zusammenfassung */}
      {data && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 sm:gap-3 mb-6" data-testid="dashboard-summary">
          <div className="card-wd p-4 flex flex-col" style={{ gap: 2 }}>
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold" style={{ color: "#64748B" }}><Building2 size={13} /> Standorte</span>
            <span className="text-2xl font-extrabold" data-testid="dashboard-total-count">{data.count}</span>
          </div>
          {STATUS.map((s) => (
            <div key={s.key} className="card-wd p-4 flex flex-col" style={{ gap: 2, background: s.bg, borderColor: s.bg }}>
              <span className="inline-flex items-center gap-1.5 text-xs font-semibold" style={{ color: s.color }}>
                <span className="inline-block rounded-full" style={{ width: 7, height: 7, background: s.dot }} /> {s.label}
              </span>
              <span className="text-2xl font-extrabold" style={{ color: s.color }} data-testid={`dashboard-total-${s.key}`}>{totals[s.key] || 0}</span>
            </div>
          ))}
        </div>
      )}

      {error && (
        <div className="card-wd p-5 mb-4" style={{ borderColor: "#DC2626" }} data-testid="dashboard-error">
          <p className="text-sm font-semibold m-0" style={{ color: "#DC2626" }}>{error}</p>
        </div>
      )}

      {loading && !data && (
        <p className="text-sm py-2" style={{ color: "#64748B" }} data-testid="dashboard-loading">Dashboard wird geladen…</p>
      )}

      {data && (data.tiles || []).length === 0 && !error && (
        <div className="card-wd p-8 text-center" data-testid="dashboard-empty">
          <Inbox size={28} className="mx-auto mb-2" style={{ color: "#94A3B8" }} />
          <p className="font-bold mb-1">Keine Standorte</p>
          <p className="text-sm" style={{ color: "#64748B" }}>Es sind keine Standorte mit Rechten vorhanden.</p>
        </div>
      )}

      {groups.map(([parent, tiles]) => (
        <div key={parent} className="mb-7" data-testid={`dashboard-group-${parent}`}>
          <div className="flex items-center gap-2 mb-3">
            <MapPin size={15} color="#F97316" />
            <h2 className="text-sm font-extrabold uppercase m-0" style={{ color: "#64748B", letterSpacing: ".04em" }}>{parent}</h2>
            <span className="text-xs font-bold px-2 py-0.5 rounded-md" style={{ background: "#F1F5F9", color: "#64748B" }}>{tiles.length}</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {tiles.map((t) => <Tile key={t.perm_id} tile={t} onOpen={openTile} />)}
          </div>
        </div>
      ))}
    </div>
  );
}

// Beibehalten für den (nicht gerouteten) Legacy-MapView-Import, damit die Auflösung nicht bricht.
export function useSummary() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/dashboard/summary").then((r) => setData(r.data)).catch(() => setData({ error: true }));
  }, []);
  return data;
}
